<script setup lang="ts">
import { type Component } from 'vue'

import '@tix/ds/styles/portico/component/card.css'

import BaseIcon from './BaseIcon.vue'

/** Figma Padding axis. */
export type BaseCardPadding = 'spacious' | 'comfy' | 'compact'
/** Figma Style axis. Named `variant` so it does not capture the native `style` attribute. */
export type BaseCardVariant = 'default' | 'subtle'
/** Figma Alignment axis on the card header. */
export type BaseCardAlign = 'left' | 'center'

export type BaseCardProps = {
  /** Tabler icon at the end of the header. Rendered only when the header slot is passed. */
  icon?: Component
  /** Figma Padding axis. Applied to the header and the body. */
  padding?: BaseCardPadding
  /** Figma Style axis. */
  variant?: BaseCardVariant
  /** Figma Shadow axis. Defaults to enabled for `default` and disabled for `subtle`. */
  shadow?: boolean
  /** Figma Alignment axis. */
  align?: BaseCardAlign
}

const props = withDefaults(defineProps<BaseCardProps>(), {
  padding: 'spacious',
  variant: 'default',
  align: 'left',
  shadow: true,
})

const slots = defineSlots<{
  /** Body content (Figma Card__Main). */
  default?: () => void
  /**
   * Header content. Pass the heading element here so the page outline stays with the consumer,
   * e.g. `<h2>Card Title</h2>`.
   */
  header?: () => void
}>()
</script>

<template>
  <div
    class="base-card"
    :class="[
      `padding-${padding}`,
      `variant-${variant}`,
      `align-${align}`,
      { shadow: props.shadow && props.variant === 'default' },
    ]"
  >
    <div v-if="slots.header" class="header">
      <div class="header-content">
        <slot name="header" />
      </div>
      <BaseIcon v-if="icon" :icon="icon" :stroke="1.5" class="icon" aria-hidden="true" />
    </div>
    <div v-if="slots.default" class="body">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.base-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  overflow: clip;
  background: var(--card-bg);
  border: var(--border-default) solid var(--card-border);
  border-radius: var(--radius-medium);
  color: var(--card-header-text);

  &.shadow {
    box-shadow: var(--card-shadow);
  }

  &.variant-subtle {
    background: var(--card-bg-subtle);
    border-color: transparent;
  }

  &.padding-spacious {
    .header,
    .body {
      padding: var(--padding-spacious);
    }
  }

  &.padding-comfy {
    .header,
    .body {
      padding: var(--padding-comfy);
    }
  }

  &.padding-compact {
    .header,
    .body {
      padding: var(--padding-compact);
    }
  }
  &:has(.body) .header {
    border-bottom: var(--border-default) solid var(--card-border);
  }

  .header {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: var(--space-1);
    width: 100%;
    background: var(--card-header-bg);
    color: var(--card-header-text);

    .header-content {
      display: flex;
      flex: 1 1 0;
      align-items: center;
      min-width: 0;

      &:deep(h1),
      &:deep(h2),
      &:deep(h3),
      &:deep(h4),
      &:deep(h5),
      &:deep(h6) {
        flex: 1 1 auto;
        min-width: 0;
        margin: 0;
        overflow: hidden;
        line-height: 1.2;
        color: var(--card-header-text);
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .icon {
      font-size: var(--size-md);
    }

    &:has(h1) .icon {
      font-size: var(--size-lg);
    }

    &:has(h3) .icon {
      font-size: var(--size-sm);
    }
  }
}

.align-center {
  .header-content {
    justify-content: center;
    text-align: center;
  }

  .header:has(.icon)::before {
    flex: 0 0 var(--space-em-1-5);
    content: '';
    font-size: var(--size-md);
  }

  .header:has(h1):has(.icon)::before {
    font-size: var(--size-lg);
  }

  .header:has(h3):has(.icon)::before {
    font-size: var(--size-sm);
  }
}
</style>
